import EntityStatus from "../components/EntityStatus";
import DeactivateButton from "../components/DeactivateButton";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import ClientCompanyForm from "../components/ClientCompanyForm";
import PageSectionCard from "../components/PageSectionCard";
import WorkstationsManager from "../components/WorkstationsManager";
import { useAppSession } from "../context/AppSessionContext";
import {
  ClientCompanyFormValues,
  toClientCompanyFormValues,
  toUpdateClientCompanyPayload,
} from "../lib/clientCompanyForm";
import { readErrorMessage } from "../lib/http";
import { ClientCompany } from "../types/clientCompanies";

const defaultValues: ClientCompanyFormValues = {
  name: "",
  registrationNumber: "",
  contactEmail: "",
  contactPhone: "",
  address: "",
};

function ClientCompanyEditPage() {
  const { id } = useParams<{ id: string }>();
  const { authorizedFetch, canManageClientCompanies } = useAppSession();
  const [isLoadingCompany, setIsLoadingCompany] = useState(true);
  const [deletedAt, setDeletedAt] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<ClientCompanyFormValues>({ defaultValues });
  const { reset } = form;
  const isSubmitting = form.formState.isSubmitting;
  const formId = "client-company-form";

  const loadCompany = useCallback(async () => {
    if (!id) {
      setLoadError("Missing client company ID in the route.");
      setIsLoadingCompany(false);
      return;
    }

    setIsLoadingCompany(true);
    setLoadError(null);

    try {
      const response = await authorizedFetch(`/client-companies/${id}`);

      if (!response.ok) {
        throw new Error(
          await readErrorMessage(
            response,
            response.status === 404
              ? "Client company was not found."
              : `Failed to load client company (${response.status}).`,
          ),
        );
      }

      const company = (await response.json()) as ClientCompany;
      setDeletedAt(company.deletedAt);
      reset(toClientCompanyFormValues(company));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to load client company.";
      setLoadError(message);
    } finally {
      setIsLoadingCompany(false);
    }
  }, [authorizedFetch, id, reset]);

  useEffect(() => {
    void loadCompany();
  }, [loadCompany]);

  const onSubmit = async (values: ClientCompanyFormValues) => {
    if (!id) {
      setSubmitError("Missing client company ID in the route.");
      return;
    }

    if (!canManageClientCompanies) {
      setSubmitError("Only manager/admin users can update client companies.");
      return;
    }

    setSubmitError(null);

    try {
      const response = await authorizedFetch(`/client-companies/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(toUpdateClientCompanyPayload(values)),
      });

      if (!response.ok) {
        const fallbackMessage =
          response.status === 401 || response.status === 403
            ? "You are not authorized to update client companies."
            : response.status === 404
              ? "Client company was not found."
              : response.status === 409
                ? "A client company with this registration number already exists."
                : `Failed to update client company (${response.status}).`;

        throw new Error(await readErrorMessage(response, fallbackMessage));
      }

      const updated = (await response.json()) as ClientCompany;
      reset(toClientCompanyFormValues(updated));
      toast.success(`Saved changes for ${updated.name}.`);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to update client company.";
      setSubmitError(message);
    }
  };

  const canSubmitForm = !isLoadingCompany && !loadError;

  return (
    <PageSectionCard
      titleId="edit-title"
      eyebrow="Client Company"
      title="Edit"
      stickyHeader
      actions={
        <div className="headerActions">
          <EntityStatus deletedAt={deletedAt} />
          <DeactivateButton
            inactive={!!deletedAt}
            endpoint={`/client-companies/${id}`}
            label="Company"
            returnTo="/client-companies"
            disabled={
              !canManageClientCompanies || !canSubmitForm || isSubmitting
            }
          />
          <button
            className="primaryButton"
            type="submit"
            form={formId}
            disabled={
              isSubmitting || !canManageClientCompanies || !canSubmitForm
            }
          >
            {isSubmitting ? "Saving..." : "Save changes"}
          </button>
        </div>
      }
    >
      {isLoadingCompany ? <p>Loading company details...</p> : null}

      {loadError ? (
        <p className="errorMessage" role="alert">
          {loadError}
        </p>
      ) : null}

      {!isLoadingCompany && !loadError ? (
        <>
          <ClientCompanyForm
            id={formId}
            form={form}
            onSubmit={onSubmit}
            submitError={submitError}
          />

          {id ? (
            <WorkstationsManager
              clientCompanyId={id}
              authorizedFetch={authorizedFetch}
              canManageClientCompanies={canManageClientCompanies}
            />
          ) : null}
        </>
      ) : null}
    </PageSectionCard>
  );
}

export default ClientCompanyEditPage;
